import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { useTranslate } from '@/hooks/useTranslate';

interface DropdownOption {
  element: React.ReactNode;
  value: string;
}

interface DropdownProps {
  options: DropdownOption[];
  onSelect: (option: DropdownOption) => void;
  selectedOption: DropdownOption | null;
  placeholder?: string;
  onClick?: () => void;
}

export const Dropdown = ({ options, onSelect, selectedOption, placeholder, onClick }: DropdownProps) => {
  const { t } = useTranslate();
  const defaultPlaceholder = placeholder ?? t('selectAnOption');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isDisabled = options.length <= 1;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (option: DropdownOption) => {
    onSelect(option);
    setIsOpen(false);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
      return;
    }
    if (!isDisabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        data-testid="dropdown-trigger"
        className={`flex flex-row items-center justify-between px-3 h-13 rounded-xl border-1 border-base-border bg-base-card transition-all duration-200 ${
          isDisabled ? '' : 'cursor-pointer'
        }`}
        onClick={handleClick}
      >
        <div className="flex flex-row items-center">
          {selectedOption ? (
            selectedOption.element
          ) : (
            <span className="text-muted-foreground">{defaultPlaceholder}</span>
          )}
        </div>
        {!isDisabled && (
          <FontAwesomeIcon
            icon={'fa-regular fa-angles-up-down'}
            className={`text-muted-foreground !text-xs transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
          />
        )}
      </div>

      <div
        className={`absolute top-full left-0 right-0 mt-1 bg-white border-1 border-base-border rounded-xl z-120 max-h-62 overflow-y-auto transition-all duration-200 ease-in-out origin-top ${
          isOpen ? 'opacity-100 scale-y-100 translate-y-0' : 'opacity-0 scale-y-0 -translate-y-2 pointer-events-none'
        }`}
      >
        {options
          .filter((option) => option.value !== selectedOption?.value)
          .map((option) => (
            <div
              key={option.value}
              className="p-3 hover:bg-base-muted cursor-pointer transition-colors duration-150"
              onClick={() => handleSelect(option)}
            >
              {option.element}
            </div>
          ))}
      </div>
    </div>
  );
};
